import { Injectable } from '@angular/core';
import { Status } from '@core/configs/status';
import { TranslateService } from '__services';
import * as moment from 'moment';
import { SwalService } from './swal.service';
import { Constants } from '../configs/constants';

@Injectable({
  providedIn: 'root',
})
export class UltiService {
  constructor(private translateService: TranslateService) {}

  confirmLeavePage(msg?: string) {
    const message =
      msg ||
      this.translateService.translate(
        'Changes you made are not saved. Are you sure you would like to leave the page?'
      );
    return confirm(message);
  }

  replaceHexCode(str): string {
    const hexCode = ['2A', '2B', '2C', '2D', '2E', '2F'];
    const chrCode = ['*', '+', ',', '-', '.', '/'];
    hexCode.forEach((hex, index) => {
      let code = `&#x${hex};`;
      str = str.replaceAll(code, chrCode[index]);
    });
    return str;
  }

  checkContainerUnique(index: number, value: string, conttainers: string[], containersError: string[]): boolean {
    if(value) {
      conttainers.forEach((cont, i) => {
        if(cont === value && i !== index) {
          containersError[index] = value;
        }
        if(containersError[index] !== value) {
          containersError[index] = null;
        }
      })
    }
    containersError.forEach((cont, i) => {
      const dup = conttainers.filter(val => val && val === cont)
      if(dup.length < 2) {
        containersError[i] = null;
      }
    })
    const hasError = containersError.some(val => val);
    return hasError
  }

  trimStrings(obj) {
    if (typeof obj === 'string' && obj != ' ') {
      return obj.trim();
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.trimStrings(item));
    }
    if (typeof obj === 'object' && obj !== null) {
      const trimmedObj = {};
      for (let key in obj) {
        trimmedObj[key] = this.trimStrings(obj[key]);
      }
      return trimmedObj;
    }
    return obj;
  }

  formatParamsFitler(param, filter) {
    const { where } = param;
    if(where) {
      filter['where'] = where;
    } else {
      delete filter['where'];
    }

    if(param.q && param.search_type) {
      let obj: any = {
        [param.search_type]: { ilike: `%${param.q}%` }
      }
      const otherStyle = ['id', 'customer_id', 'flow_id', 'company_other_id', 'consignee_id', 'contract_id', 'trucking_company_id'];
      if(otherStyle.includes(param.search_type)) {
        obj = {
          "or":[{[param.search_type]: {ilike: `%${param.q}%`}}]
        }
      }

      if(where) {
        obj = {...where, ...obj};
        delete param.where;
      }
      // if(param.where) {
      //   obj = {...obj, ...param.where};
      //   delete param.where;
      // }
      filter = {...filter, where: {...obj}}
      delete param.q;
      // delete param.search_type;
      // delete obj.search_type;
    }
    filter = {...filter,...param};
    // delete filter.q;
    delete filter.search_type;
    return filter
  }

  onFilter(data, filter, filterParams, isFirstLoad, routeQuery, pIndex, pSize ) {
    const { params, formFilter } = data;
    filter = formFilter;
    filterParams = this.formatParamsFitler(params, filterParams);
    if(filterParams.where && filterParams.where.location_id === 'null'){
      filterParams.where.location_id = null;
    }
    if(isFirstLoad) {
      const { pageIndex, pageSize } = routeQuery;
      pIndex = pageIndex ?? 0;
      pSize = pageSize ?? Constants.PageSizeOptions[0];
      filterParams.offset = pSize * pIndex;
    } else {
      pIndex = 0
      filterParams.offset = 0;
    }
    return { pIndex, pSize }
  }

  showWarningContainerNo(data) {
    const { matched_flows, status_all } = data;
    if(!matched_flows || matched_flows.length === 0) return;
    const flows = matched_flows.split(',');
    const validationFlows = this.convertFlows(flows, `-${Status.ComplianceFlowEnum.ForValidation}`);
    const liveFlows = this.convertFlows(flows, `-${Status.ComplianceFlowEnum.Live}`);
    let msg = '';
    switch (status_all) {
      case 1:
        msg = this.translateService.translate(
          'This is a warning that the container number entered seems to be under for validation for flow Id {{validationFlows}}', {validationFlows}
        )
        break;
      case 2:
        msg = this.translateService.translate(
          'This is a warning that the container number entered seems to be on a live journey for journey Id {{liveFlows}}', {liveFlows}
        )
        break;
      case 3:
        msg = this.translateService.translate(
          'This is a warning that the container number entered seems to be under for validation for flow Id {{validationFlows}} & live journey for journey Id {{liveFlows}}', {validationFlows, liveFlows}
        )
        break;
    }
    SwalService.warning(msg);
  }

  private convertFlows(flows, status: string) {
    const filtered = flows.filter(flow => flow.includes(status));
    const removedStatus = filtered.map(flow => flow.replace(status, ''));
    const joinFlows = removedStatus.join(', ');
    return joinFlows;
  }

  /**  Displayed as what is entered by the user (API always returns UTC+0)
   *@param date (string with format YYYY-MM-DDTHH:MM:SS.000Z)
  */
  stringToDate(date: string) {
    if(date) {
      const str = moment.utc(date).format('YYYY-MM-DD HH:mm:ss');
      return new Date(str);
    }
    return null
  }

  stringToDateNoUtc(date: string) {
    if(date) {
      const str = moment(date).format('YYYY-MM-DD HH:mm:ss');
      return new Date(str);
    }
    return null
  }

  /**  Displayed as what is entered by the user (send to API UTC+0)
   *@param date (Date)
  */
  dateToString(date: Date | moment.Moment | null) {
    if(date) {
      const format = moment(date).format('YYYY-MM-DDTHH:mm');
      const str = `${format}:00.000Z`;
      return str;
    }
    return null
  }

  getCurrentDate(format: string = 'MM/DD/YYYY') {
    return moment().format(format);
  }
  
  addDays(date, days, format?: string) {
    const result = moment(date).add(days, 'days');
    if(format) {
      return result.format(format);
    }
    return result;
  }

  formatDate(date, format) {
    return moment(date).format(format);
  }
}
